import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NameHintInline.module.css';

interface NameHintInlineProps {
  triggerActive: boolean;
  isPencilEditing: boolean;
  onKeepGeneral: () => void | Promise<void>;
  onRequestRename: () => void;
}

/**
 * Inline soft hint rendered under PrayerDetail's `who` row. The parent
 * computes `shouldShowNameHint(prayer, prayers, language)` once per
 * render and passes the boolean as `triggerActive` — this component
 * is presentational + state-machine, no trigger evaluation of its own.
 * Single source of truth at the call site means no drift between the
 * hint's render gate and any sibling affordance keyed off the same
 * trigger (e.g. PrayerDetail's lit-up Edit button).
 *
 * Two states:
 *  - 'hint': single italic dim-gold tappable line inviting the user
 *    to name the subject.
 *  - 'expanded': two-chip row inside a soft thin-border container —
 *    "Keep general" and "Rename". Outside-tap or Escape collapses.
 *
 * Renders null when `triggerActive` is false OR when `isPencilEditing`
 * is true (no competing affordances on screen). The caller MUST also
 * key this component on `editingWho` so `mode` resets to 'hint' on
 * every pencil cycle — see PrayerDetail integration.
 *
 * Commit path for chip 1 ("Keep general") matches the rename pencil
 * exactly via the caller's `onKeepGeneral` handler (immediate marker
 * write + fire-and-forget reparse). This component does not write to
 * the prayer.
 */
export default function NameHintInline({
  triggerActive,
  isPencilEditing,
  onKeepGeneral,
  onRequestRename,
}: NameHintInlineProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'hint' | 'expanded'>('hint');
  const rootRef = useRef<HTMLDivElement>(null);

  // Document-level click-outside + Escape collapse, attached only while
  // expanded. Inline component, no backdrop — document listener is the
  // standard pattern (Phase 2 Q3 ratification: rejected explicit Cancel
  // chevron to avoid chrome competition with the dim-gold copy).
  useEffect(() => {
    if (mode !== 'expanded') return;
    const handlePointer = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      setMode('hint');
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMode('hint');
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [mode]);

  if (isPencilEditing) return null;
  if (!triggerActive) return null;

  if (mode === 'hint') {
    return (
      <div ref={rootRef} className={styles.wrap}>
        <button
          type="button"
          className={styles.hintButton}
          onClick={() => setMode('expanded')}
          aria-label={t('nameHint.hintAriaLabel')}
        >
          {t('nameHint.hintCopy')}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`${styles.wrap} ${styles.expandedWrap}`}
      role="group"
      aria-label={t('nameHint.expandedAriaLabel')}
    >
      <button
        type="button"
        className={styles.chip}
        onClick={(e) => {
          // Defensive stopPropagation: the document mousedown listener
          // also fires on chip taps; the chip's intent should be
          // unambiguous even though the parent will remount this
          // component via the `key` reset before order matters.
          e.stopPropagation();
          void onKeepGeneral();
        }}
      >
        {t('nameHint.keepGeneralChip')}
      </button>
      <button
        type="button"
        className={`${styles.chip} ${styles.chipRename}`}
        onClick={(e) => {
          e.stopPropagation();
          onRequestRename();
        }}
      >
        {t('nameHint.renameChip')}
      </button>
    </div>
  );
}
